import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import './Register.css';

interface PaymentResultProps {
  setToken?: (token: string) => void;
}

const PaymentResult: React.FC<PaymentResultProps> = ({ setToken }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const status = searchParams.get('status') || 'failed';
  const paymentId = searchParams.get('payment_id');
  const isSuccess = status === 'success';
  const [autoLogged, setAutoLogged] = useState(false);
  const [loading, setLoading] = useState(isSuccess);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '') || '';

  useEffect(() => {
    if (!isSuccess || !paymentId) {
      setLoading(false);
      return;
    }

    // Try auto-login: get token from payment, then authenticate
    const tryAutoLogin = async () => {
      try {
        // Small delay to let webhook activate the user
        await new Promise(r => setTimeout(r, 2000));

        const tokenRes = await api.get(`/chargily/auto-login-token/${paymentId}`);
        const autoLoginToken = tokenRes.data?.token;
        if (!autoLoginToken) throw new Error('No token');

        const loginRes = await fetch(`${API_BASE}/auth/auto-login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: autoLoginToken }),
        });

        if (loginRes.ok) {
          const data = await loginRes.json();
          if (data.access_token) {
            localStorage.setItem('token', data.access_token);
            if (setToken) setToken(data.access_token);
            setAutoLogged(true);
          }
        }
      } catch (err) {
        console.error('Auto-login after payment failed:', err);
      } finally {
        setLoading(false);
      }
    };

    tryAutoLogin();
  }, [isSuccess, paymentId, API_BASE, setToken]);

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
        <div className="success-modal" style={{ position: 'relative', maxWidth: '480px' }}>
          <div className="success-modal-icon">
            <div className="spinner-border text-danger" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
          <p className="success-modal-message">
            {i18n.language === 'ar' ? 'جاري تفعيل حسابك...' : 'Activation de votre compte en cours...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
      <div className="success-modal" style={{ position: 'relative', maxWidth: '480px' }}>
        <button
          className="success-modal-close"
          onClick={() => navigate(`/${i18n.language}`)}
          aria-label="Fermer"
        >
          <i className="bi bi-x-lg"></i>
        </button>

        <div className="success-modal-icon">
          {isSuccess ? (
            <i className="bi bi-check-circle-fill" style={{ color: '#28a745' }}></i>
          ) : (
            <i className="bi bi-x-circle-fill" style={{ color: '#dc3545' }}></i>
          )}
        </div>

        <p className="success-modal-message">
          {isSuccess
            ? (i18n.language === 'ar'
              ? autoLogged
                ? 'تم الدفع بنجاح! حسابك مفعل وأنت متصل الآن.'
                : 'تم الدفع بنجاح! حسابك مفعل الآن.'
              : autoLogged
                ? 'Paiement effectu\u00e9 avec succ\u00e8s ! Votre compte est activ\u00e9 et vous \u00eates connect\u00e9.'
                : 'Paiement effectu\u00e9 avec succ\u00e8s ! Votre compte est activ\u00e9.')
            : (i18n.language === 'ar'
              ? 'فشل الدفع أو تم إلغاؤه. يرجى المحاولة مرة أخرى أو اختيار طريقة دفع أخرى.'
              : 'Le paiement a \u00e9chou\u00e9 ou a \u00e9t\u00e9 annul\u00e9. Veuillez r\u00e9essayer ou choisir un autre mode de paiement.')
          }
        </p>

        <div className="d-flex gap-2 justify-content-center mt-3">
          {isSuccess ? (
            <button
              className="btn btn-success"
              onClick={() => navigate(`/${i18n.language}`)}
            >
              {i18n.language === 'ar' ? 'الصفحة الرئيسية' : 'Accueil'}
            </button>
          ) : (
            <>
              <button
                className="btn btn-outline-secondary"
                onClick={() => navigate(`/${i18n.language}`)}
              >
                {i18n.language === 'ar' ? 'الصفحة الرئيسية' : 'Accueil'}
              </button>
              <button
                className="btn btn-primary"
                onClick={() => navigate(`/${i18n.language}/register`)}
              >
                {i18n.language === 'ar' ? 'إعادة المحاولة' : 'R\u00e9essayer'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentResult;
